import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, X, FileText, Image as ImageIcon, File as FileIcon, Ruler, Check } from 'lucide-react';
import { Label } from '../ui/label';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('access_token')}` });

export const fileIcon = (ct = '') => (ct.includes('pdf') ? FileText : ct.startsWith('image/') ? ImageIcon : FileIcon);
export const fmtSize = (n) => (!n ? '' : n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);
export const fmtDateBR = (d) => (d && /^\d{4}-\d{2}-\d{2}/.test(d) ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : d || '');

// Lista de anexos (PDFs, fotos, planilhas…) de um trabalho MEASURE. value = [{file_id, filename, content_type, size}]
export const MeasureFilesField = ({ value = [], onChange, language = 'pt-BR' }) => {
  const [busy, setBusy] = useState(false);
  const pt = language === 'pt-BR';
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setBusy(true);
    const added = [];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        const { data } = await axios.post(`${API}/api/schedules/upload-file`, fd, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
        added.push({ file_id: data.file_id, filename: data.filename, content_type: data.content_type, size: data.size, uploaded_at: data.uploaded_at });
      } catch (err) {
        toast.error(`${pt ? 'Falha ao enviar' : 'Upload failed'}: ${f.name}`);
      }
    }
    if (added.length) onChange([...(value || []), ...added]);
    setBusy(false);
  };
  return (
    <div data-testid="measure-files-field">
      <Label>📎 {pt ? 'Arquivos da medição (PDF, fotos, etc.)' : 'Measurement files (PDF, photos, etc.)'} <span className="text-xs font-normal text-gray-500">({pt ? 'opcional' : 'optional'})</span></Label>
      {value?.length > 0 && (
        <ul className="mt-2 space-y-1" data-testid="measure-files-list">
          {value.map((f) => {
            const Icon = fileIcon(f.content_type);
            return (
              <li key={f.file_id} className="flex items-center gap-2 rounded-lg border border-purple-200 bg-purple-50 px-2 py-1.5 text-xs" data-testid={`measure-file-${f.file_id}`}>
                <Icon className="h-4 w-4 text-purple-700 shrink-0" />
                <a href={`${API}/api/files/${f.file_id}`} target="_blank" rel="noreferrer" className="flex-1 truncate text-purple-900 hover:underline" data-testid={`measure-file-open-${f.file_id}`}>{f.filename}</a>
                <span className="text-purple-500">{fmtSize(f.size)}</span>
                <button type="button" onClick={() => onChange(value.filter((x) => x.file_id !== f.file_id))} className="text-purple-500 hover:text-red-600" aria-label={pt ? 'Remover' : 'Remove'} data-testid={`measure-file-remove-${f.file_id}`}><X className="h-3.5 w-3.5" /></button>
              </li>
            );
          })}
        </ul>
      )}
      <input type="file" multiple onChange={upload} className="hidden" id="measure-files-upload" data-testid="measure-files-input" />
      <label htmlFor="measure-files-upload" className="mt-2 flex items-center justify-center gap-2 p-3 border-2 border-dashed border-purple-300 rounded-lg cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-colors text-sm text-purple-700">
        <Upload className="h-4 w-4" /> {busy ? (pt ? '⏳ Enviando…' : '⏳ Uploading…') : (pt ? 'Adicionar arquivos' : 'Add files')}
      </label>
    </div>
  );
};

// Campos que a instalação herda da medição
export const measureToForm = (m) => ({
  customer_name: m.customer_name || '', customer_email: m.customer_email || '', order_number: m.order_number || '', tech_measure: m.tech_measure || '',
  address: m.address || '', city: m.city || '', zip_code: m.zip_code || '', year_built: m.year_built || '',
  windows: m.windows || 0, doors: m.doors || 0, measure_job_id: m.id,
});

// Select "Baseado em medição" (INSTALL/SERVICE): escolher uma medição preenche o formulário e cria o vínculo (selo "Trabalho medido")
export const MeasureLinkField = ({ value, onPick, onClear, language = 'pt-BR' }) => {
  const [measures, setMeasures] = useState([]);
  const pt = language === 'pt-BR';
  useEffect(() => {
    axios.get(`${API}/api/schedules/measured`, { headers: authHeaders() }).then((r) => setMeasures(r.data || [])).catch(() => setMeasures([]));
  }, []);
  const linked = measures.find((m) => m.id === value);
  const options = measures.filter((m) => !m.installed_job || m.id === value);
  if (!measures.length) return null;
  return (
    <div className="rounded-lg border border-purple-200 bg-purple-50/60 p-3" data-testid="measure-link-field">
      <Label className="flex items-center gap-1.5 text-purple-900"><Ruler className="h-4 w-4" /> {pt ? 'Baseado em medição' : 'Based on measurement'}</Label>
      {linked ? (
        <div className="mt-2 flex items-center gap-2 text-xs" data-testid="measure-link-selected">
          <span className="inline-flex items-center gap-1 rounded-full bg-purple-600 text-white px-2 py-0.5 font-semibold"><Check className="h-3 w-3" /> {pt ? 'Trabalho medido' : 'Measured job'}</span>
          <span className="text-purple-900 truncate">{linked.customer_name} · {fmtDateBR(linked.date)}{linked.order_number ? ` · #${linked.order_number}` : ''}</span>
          <Button type="button" variant="ghost" size="sm" className="ml-auto h-7 px-2 text-purple-700" onClick={onClear} data-testid="measure-link-clear"><X className="h-3.5 w-3.5" /></Button>
        </div>
      ) : (
        <select className="mt-2 w-full h-10 rounded-md border border-purple-200 bg-white px-2 text-sm" value="" onChange={(e) => { const m = measures.find((x) => x.id === e.target.value); if (m) onPick(m); }} data-testid="measure-link-select">
          <option value="">{pt ? 'Escolher uma medição…' : 'Pick a measurement…'}</option>
          {options.map((m) => <option key={m.id} value={m.id}>{fmtDateBR(m.date)} · {m.customer_name}{m.order_number ? ` · #${m.order_number}` : ''} · 🪟{m.windows || 0} 🚪{m.doors || 0}</option>)}
        </select>
      )}
      <p className="text-[11px] text-purple-700 mt-1">{pt ? 'Preenche cliente, endereço, janelas/portas da medição e marca o trabalho como "medido".' : 'Fills customer, address, windows/doors from the measurement and flags the job as "measured".'}</p>
    </div>
  );
};
